import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({
  baseURL: API,
  headers: { "Content-Type": "application/json" },
});

export const BUSINESS = {
  name: "Northwestern Exterior Cleaning",
  short: "Northwestern",
  area: "North Seattle, WA",
  email: "mberg@northwesternexteriorcleaning.com",
  tagline: "Concrete, restored.",
};

// Display-only material catalogue. The actual $/sqft + multipliers come from /api/pricing.
export const MATERIALS = [
  { id: "concrete",  label: "Concrete",       hint: "Driveways, walkways, patios" },
  { id: "brick",     label: "Brick",          hint: "Brick patios, walkways" },
  { id: "pavers",    label: "Pavers",         hint: "Stone or interlocking pavers" },
  { id: "composite", label: "Composite",      hint: "Trex / composite decking" },
  { id: "wood",      label: "Wood",           hint: "Cedar / pine decks & fences" },
  { id: "hybrid",    label: "Hybrid / Mixed", hint: "Multiple surfaces — wider range" },
];

export const STAIN_SEVERITY = [
  { id: "light",  label: "Light",  hint: "Mostly dust + recent dirt" },
  { id: "medium", label: "Medium", hint: "Visible mildew, tire marks" },
  { id: "heavy",  label: "Heavy",  hint: "Oil, rust, deep-set stains" },
];

const RANGE_BAND = 0.15;
export const OWNER_PHONE = "(206) 801-0862";
// Customers can't measure their property exactly — we ask them to pick a 250-sqft bucket.
// Marlowe measures on-site and locks in the final price.
export const BUCKET_SIZE = 250;
export const SQFT_MAX = 8000;

function _tier(sqft, breakpoints) {
  if (sqft <= 0) return "—";
  if (sqft <= (breakpoints?.small_max ?? 1000)) return "Small";
  if (sqft <= (breakpoints?.medium_max ?? 3000)) return "Medium";
  return "Large";
}

function _multiplier(severity, pricing) {
  const m = pricing?.stain_multipliers || {};
  return Number(m[severity] ?? m.light ?? 1.0);
}

/** Quote at a single sqft point using the dynamic pricing config. */
export function quoteFor(sqft, material = "concrete", severity = "light", pricing = null) {
  const n = Number(sqft) || 0;
  const cap = Number(pricing?.estimate_sqft_cap ?? 5000);
  const overCap = n > cap;
  if (n <= 0) {
    return { tier: "—", rate: 0, total: 0, mid: 0, low: 0, high: 0, material, severity, band: RANGE_BAND, overCap: false };
  }
  const tier = _tier(n, pricing?.tier_breakpoints);
  const rates = pricing?.rates || {};
  const matKey = rates[material] || material === "hybrid" ? material : "concrete";
  const sev = _multiplier(severity, pricing);

  if (matKey === "hybrid") {
    const hr = pricing?.hybrid_rates?.[tier] || { low: 0.2, high: 0.4 };
    const low = Math.round(n * hr.low * sev);
    const high = Math.round(n * hr.high * sev);
    const mid = Math.round((low + high) / 2);
    return {
      tier, rate: (hr.low + hr.high) / 2, total: mid, mid, low, high,
      material: matKey, severity, band: mid ? (high - low) / (2 * mid) : RANGE_BAND, overCap,
    };
  }

  const tierRates = rates[matKey] || rates.concrete || {};
  const rate = Number(tierRates[tier] ?? 0);
  const mid = n * rate * sev;
  return {
    tier, rate, total: mid, mid,
    low: Math.round(mid * (1 - RANGE_BAND)),
    high: Math.round(mid * (1 + RANGE_BAND)),
    material: matKey, severity, band: RANGE_BAND, overCap,
  };
}

/** Snap a raw slider value down to the nearest BUCKET_SIZE multiple. */
export function snapToBucket(rawSqft) {
  const n = Math.max(0, Number(rawSqft) || 0);
  return Math.floor(n / BUCKET_SIZE) * BUCKET_SIZE;
}

/**
 * Quote for a sqft bucket [bucketLow, bucketLow+BUCKET_SIZE].
 *
 * Range corresponds DIRECTLY to the bucket edges — no extra ±15% band on top
 * (the bucket itself is the uncertainty). Tier reflects the high edge.
 */
export function bucketQuoteFor(bucketLow, material = "concrete", severity = "light", pricing = null) {
  const raw = snapToBucket(bucketLow);
  const lo = Math.min(raw, SQFT_MAX - BUCKET_SIZE);
  const hi = lo + BUCKET_SIZE;
  const cap = Number(pricing?.estimate_sqft_cap ?? 5000);

  if (hi > cap) {
    return {
      bucketLow: lo, bucketHigh: hi, overCap: true,
      tier: _tier(hi, pricing?.tier_breakpoints), low: 0, high: 0,
      material, severity,
    };
  }

  const isHybrid = material === "hybrid";
  const qLo = lo > 0 ? quoteFor(lo, material, severity, pricing) : { mid: 0, low: 0, tier: "—" };
  const qHi = quoteFor(hi, material, severity, pricing);

  return {
    bucketLow: lo,
    bucketHigh: hi,
    tier: qHi.tier,
    low:  isHybrid ? qLo.low  : Math.round(qLo.mid),
    high: isHybrid ? qHi.high : Math.round(qHi.mid),
    overCap: false,
    material: qHi.material,
    severity: qHi.severity,
  };
}

/** Add-on subtotal for a single bucket-high job. Returns { total, applied: [...] }.
 *
 * `selections` accepts:
 *   - ["paver_resand"]                    (per-sqft toggles)
 *   - [{ id: "waste_bin_wash", units: 3 }] (per-unit picks)
 */
export function computeAddons(selections, sqft, material, pricing) {
  const out = { total: 0, applied: [] };
  const addons = pricing?.addons || {};
  const mat = (material || "concrete").toLowerCase();
  for (const raw of selections || []) {
    const sel = typeof raw === "string" ? { id: raw, units: 1 } : raw;
    const cfg = addons[sel.id];
    if (!cfg || !cfg.enabled) continue;
    const allowed = cfg.applies_to_materials || [];
    if (allowed.length && !allowed.includes(mat)) continue;
    const ptype = cfg.pricing_type || "per_sqft";
    if (ptype === "per_unit") {
      let units = Math.max(0, Math.floor(Number(sel.units) || 0));
      const maxUnits = Math.floor(Number(cfg.max_units) || 0);
      if (maxUnits > 0) units = Math.min(units, maxUnits);
      if (units <= 0) continue;
      const rate = Number(cfg.rate_per_unit || 0);
      const amount = Math.round(rate * units * 100) / 100;
      if (amount <= 0) continue;
      out.total += amount;
      out.applied.push({
        id: sel.id, label: cfg.label || sel.id, pricing_type: "per_unit",
        rate_per_unit: rate, unit_label: cfg.unit_label || "unit", units, amount,
      });
    } else {
      const rate = Number(cfg.rate_per_sqft || 0);
      const amount = Math.round(rate * Math.max(0, sqft) * 100) / 100;
      if (amount <= 0) continue;
      out.total += amount;
      out.applied.push({
        id: sel.id, label: cfg.label || sel.id, pricing_type: "per_sqft",
        rate_per_sqft: rate, amount,
      });
    }
  }
  out.total = Math.round(out.total * 100) / 100;
  return out;
}
